import { DynamicResponse } from 'utils/api-utils/api-utils';
import { ApplicationApprovedResponse } from '../../types/responses/ApplicationApprovedResponse';
import { ApplicationPendingResponse } from '../../types/responses/ApplicationPendingResponse';
import { GetApplicationAuthorizedResponse } from '../../types/responses/GetApplicationAuthorizedResponse';
import { VerifyIdentificationDocumentResponse } from '../../types/responses/VerifyIdentificationDocumentResponse';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import { AcquisitionFlow, getAcquisitionFlow, getAppData } from '../../utils/acquisition-utils';
import SessionStorage from '../../utils/SessionStorage';
import { usePushdownContext } from '../Pushdown/PushdownProvider';
import { useMitekContext } from './MitekProvider';
import { redirect, clearSessionAndRedirectToDataEntry } from './MitekUtils';

// Explicit use of 'any' because the types from API responses or errors are not 
// documented.
export type ActionOptions = {
    onFailure?: (response?: DynamicResponse<any>) => void,
    onSuccess?: (response?: DynamicResponse<any>) => void
}
type NextAction = ((options?: ActionOptions) => Promise<NextAction>) | undefined;


export function useMitekSubmit() {
    const { showPushdown } = usePushdownContext();
    const {
        MitekProps,
        MitekConfiguration,
        retrieveResultsToSubmit
    } = useMitekContext();

    async function fullMitekSubmit(options?: ActionOptions) {
        // TODO: Full submit of all documents
        // Beware all ye who enter, careless cases can lead to infinite loops
        let nextAction = callVerifyIdentificationDocumentApi;
        while (nextAction) {
            nextAction = await nextAction(options);
        }
    }

    // Mitek Specific Start
    async function callVerifyIdentificationDocumentApi(options?: ActionOptions): Promise<NextAction> {

        const attemptSubmit = async () => {
            const requestObject = retrieveResultsToSubmit();
            const response = await acquisitionApiRequest<VerifyIdentificationDocumentResponse>(
                'verify-identification-document',
                { body: requestObject },
            );

            if (
                MitekConfiguration.current.isMobileOnlySession
                && response.data.SubmitApplicationEnum
            ) {
                options?.onSuccess && options.onSuccess(response);

                switch (parseInt(response.data.SubmitApplicationEnum)) {
                    case 1: // DeclinedWithEsignCredit
                    case 2: // DeclinedWithEsignCreditNoScore
                    case 3: // DeclinedWithEsignNonCredit
                    case 4: // DeclindWithESignNCDLetter
                        SessionStorage.remove('isCounterOffer');
                        redirect(MitekProps.limitedEsignPageUrl, {
                            withoutBackNavigation: true,
                        });
                        return;
                    case 5: // DeclinedWithEsignMLA
                        // Currently Unused?
                        SessionStorage.remove('isCounterOffer');
                        redirect(MitekProps.limitedEsignPageUrl, {
                            withoutBackNavigation: true,
                        });
                        return;
                    case 6: // DeclinedWithEsignOFAD
                        // Possible but not listed?
                        SessionStorage.remove('isCounterOffer');
                        redirect(MitekProps.limitedEsignPageUrl, {
                            withoutBackNavigation: true,
                        });
                        return;
                    case 7: // Approved
                        return callApplicationApproved;
                    case 8: // Pending
                        return callApplicationPending;
                    case 9: // Authorize
                        if (response?.data?.IsRetake === true) { // Stay in Mitek Flow
                            return;
                        }
                        return callApplicationAuthorized;
                    case 15: // OK?
                        // Currently unused?
                        clearSessionAndRedirectToDataEntry(
                            MitekProps.dataEntryPageUrl,
                            MitekProps.errorApiPushdownCode,
                        );
                        return;
                    default:
                        throw 'Unable to resolve submission result'
                }
            } else if (response.status === 200) {
                options?.onSuccess && options.onSuccess(response);
            } else {
                throw response;
            }

            return;
        }

        try {
            for (let i = MitekProps.mitekRetryAmount ?? 1; i-- > 0;) {
                let result: (options?: ActionOptions) => Promise<NextAction>;
                try {
                    result = await attemptSubmit();
                    if (!result) {
                        throw new Error('Submit attempt returned null or undefined');
                    }
                    return result;
                } catch (innerError: unknown) {
                    if (
                        typeof innerError === 'object' &&
                        innerError !== null &&
                        'status' in innerError &&
                        typeof (innerError as Record<string, unknown>).status === 'number'
                    ) {
                        // Explicit use of 'any' because the error response type for the verify-identification-document API
                        // is not documented. We must pass the response object and be able to access it's unknown values in
                        // subsequent function calls.
                        const verifyIdentificationDocumentError = innerError as DynamicResponse<any> & Record<string, unknown>;

                        if ([401, 404, 500, 501, 502, 503, 504].includes(verifyIdentificationDocumentError?.status)) {
                            options?.onFailure && options.onFailure(innerError as DynamicResponse<any>);
                            throw innerError;
                        }
                    }

                    // hit outer catch. no more retries.
                    if (i <= 0) {
                        throw innerError;
                    }

                    // continue retry attempts
                }
            }
        } catch (error: unknown) {
            // exit execution if no more attempts are available
            clearSessionAndRedirectToDataEntry(
                MitekProps.dataEntryPageUrl,
                MitekProps.errorApiPushdownCode,
            );
        }
        return;
    }

    // Mitek Specific End

    // Application Approved Start

    const AppApprovedEndpoints: Record<AcquisitionFlow, string> = {
        preQual: 'pq-app-approved',
        preApproved: 'pa-app-approved',
        skipPQ: 'skip-pq-app-approved',
        pqOne: 'skip-pq-app-approved',
        partnerHosted: '',
        [AcquisitionFlow.SkipPQCounter]: undefined,
    };

    async function callApplicationApproved(): Promise<NextAction> {
        // Assuming this is occurring in Mobile Only Application
        try {
            const response = await acquisitionApiRequest<ApplicationApprovedResponse>(
                AppApprovedEndpoints[getAcquisitionFlow()],
                {},
            );
            await SessionStorage.setCipher('app-approved-data', response.data);
            const appData = await getAppData();
            const isSecuredCard = appData.IsSecuredCard;
            const fullAppResponse = await SessionStorage.getCipher('fullAppResponse');
            const isRiskBasedPricingNoticeRequired =
                fullAppResponse && 'isRiskBasedPricingNoticeRequired' in fullAppResponse
                    ? fullAppResponse.isRiskBasedPricingNoticeRequired
                    : false;
            const isCounterOffer = SessionStorage.get('isCounterOffer') === 'true';

            if ('InstantIssuanceOffer' in response.data) {
                switch (response.data.InstantIssuanceOffer) {
                    case 0: // None, No special offer
                    case 1: { // Instant Issuance Approved
                        if (isSecuredCard) {
                            redirect(MitekProps.pendingFundingUrl, {
                                withoutBackNavigation: true,
                            });
                        } else if (!isCounterOffer && isRiskBasedPricingNoticeRequired) {
                            redirect(MitekProps.riskBasedEsignUrl, {
                                withoutBackNavigation: true,
                            });
                        } else {
                            redirect(MitekProps.confirmationPageUrl, {
                                withoutBackNavigation: true,
                            });
                        }
                        return;
                    }
                    case 2: { // Mobile Wallet Approved
                        const isSignedEsign = SessionStorage.verifyNotEmpty('isEsign') && SessionStorage.getObject('isEsign');
                        const isSignedLimitedEsign = SessionStorage.verifyNotEmpty('visitedEsign') && SessionStorage.getObject('visitedEsign');
                        SessionStorage.set('rbPricingRequired', isRiskBasedPricingNoticeRequired);

                        const redirectUrl = isRiskBasedPricingNoticeRequired && !isSignedEsign && !isSignedLimitedEsign ? MitekProps.riskBasedEsignUrl : MitekProps.mobileWalletPageUrl;
                        redirect(redirectUrl, { withoutBackNavigation: true });
                        return;
                    }

                }
            }

            throw 'Malformed Response';
        } catch (error) {
            // Non 200 response or other
            showPushdown(MitekProps.errorApiPushdownCode);
        }
        return;
    }

    // Application Approved End

    // Application Pending Start

    const AppPendingEndpoints: Record<AcquisitionFlow, string> = {
        preQual: 'pq-app-pending',
        preApproved: 'pa-app-pending',
        skipPQ: 'skip-pq-app-pending',
        pqOne: 'skip-pq-app-pending',
        partnerHosted: '',
        [AcquisitionFlow.SkipPQCounter]: undefined,
    };

    async function callApplicationPending(): Promise<NextAction> {
        try {
            const response = await acquisitionApiRequest<ApplicationPendingResponse>(
                AppPendingEndpoints[getAcquisitionFlow()],
                {},
            );
            await SessionStorage.setCipherWithCurrentScope(
                'app-pending-data',
                response.data,
            );
            if (response.status !== 200) {
                throw 'Error with pending api';
            }

            redirect(MitekProps.pendingApprovalPageUrl, {
                withoutBackNavigation: true,
            });
        } catch (error) {
            showPushdown(MitekProps.errorApiPushdownCode);
        }
        return;
    }

    // Application Pending End

    // Application Authorized Start

    async function callApplicationAuthorized(): Promise<NextAction> {
        try {
            const response =
                await acquisitionApiRequest<GetApplicationAuthorizedResponse>(
                    'get-application-authorized',
                    {},
                );
            await SessionStorage.setCipherWithCurrentScope(
                'appAuthorizedResponse',
                response.data,
            );
            if ('NextAction' in response.data) {
                switch (response.data.NextAction) {
                    case '0':
                        clearSessionAndRedirectToDataEntry(
                            MitekProps.dataEntryPageUrl,
                            'appInProgress',
                        );
                        return;
                    // TODO: This shouldn't be possible from what I understand, double check and remove if so
                    case '1': // KIQ / Level 3 Questions
                        redirect(MitekProps.levelThreeQuestionsPageUrl, {
                            withoutBackNavigation: true,
                        });
                        return;
                    // TODO: This shouldn't be possible from what I understand, double check and remove if so
                    case '2': // One-Time Passcode (OTP)
                        redirect(MitekProps.oneTimePasscodePageUrl, {
                            withoutBackNavigation: true,
                        });
                        return;
                }
            }
            SessionStorage.remove('appAuthorizedResponse');
            throw 'Malformed Response';
        } catch (error) {
            showPushdown(MitekProps.errorApiPushdownCode);
        }
        return;
    }

    // Application Authorized End

    return {
        fullMitekSubmit,
    };
}

export default useMitekSubmit;
